/**
 * TimelineAPIHandler - Handles API interactions for the TimelineCalendar
 * Manages connection to backend services, data loading and fallback mechanisms
 */
class TimelineAPIHandler {
  /**
   * Create a new TimelineAPIHandler
   * @param {TimelineCalendar} timeline - The timeline instance
   */
  constructor(timeline) {
    this.timeline = timeline;
    this.connectionStatus = 'disconnected';
    this.statusElement = document.getElementById('connection-status');
    this.lastErrorMessage = '';
    this.retryAttempts = 0;
    this.maxRetryAttempts = 3;
    this.retryDelay = 2000;
  }

  /**
   * Update the connection status indicator
   * @param {string} status - Connection status ('connected', 'disconnected', 'connecting')
   * @param {string} message - Optional status message
   */
  updateConnectionStatus(status, message = '') {
    this.connectionStatus = status;

    if (this.statusElement) {
      if (status === 'connected') {
        this.statusElement.textContent = this.timeline.t('Connected');
        this.statusElement.className = 'status-indicator connected';
      } else if (status === 'disconnected') {
        this.statusElement.textContent = this.timeline.t('Connection failed');
        this.statusElement.className = 'status-indicator disconnected';
      } else if (status === 'connecting') {
        this.statusElement.textContent = this.timeline.t('Connecting...');
        this.statusElement.className = 'status-indicator';
      }
    }

    if (message) {
      this.lastErrorMessage = message;
    }
  }

  /**
   * Check API connection status
   * @returns {Promise<Object>} Connection check result
   */
  async checkConnection() {
    try {
      const response = await fetch(`${this.timeline.apiBase}ping`, {
        method: 'GET',
        headers: {'Content-Type': 'application/json'}
      });

      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      const result = await response.json();

      if (result && result.success) {
        this.updateConnectionStatus('connected');
        return {success: true};
      } else {
        this.updateConnectionStatus('disconnected', result.error || 'Unknown error');
        return {success: false, error: result.error};
      }
    } catch (error) {
      console.error('API connection check failed:', error);
      this.updateConnectionStatus('disconnected', error.message);
      return {success: false, error: error.message};
    }
  }

  /**
   * Load a project from the server
   * @param {number|string} projectId - Project ID to load
   * @returns {Promise<Object>} - Project data
   */
  async loadProject(projectId) {
    try {
      this.updateConnectionStatus('connecting');

      let data;
      if (window.ApiService) {
        data = await ApiService.get(`${this.timeline.apiBase}load_timeline`, { project_id: projectId });
      } else {
        const response = await fetch(`${this.timeline.apiBase}load_timeline?project_id=${projectId}`, {
          method: 'GET',
          headers: {'Content-Type': 'application/json'},
          signal: AbortSignal.timeout(10000)
        });
        if (!response.ok) {
          throw new Error(this.timeline.t('Server responded with status: {status}', {status: response.status}));
        }
        data = await response.json();
      }

      if (data.error) {
        throw new Error(data.error);
      }

      this.updateConnectionStatus('connected');
      this.retryAttempts = 0;

      // load_timeline ตอบเป็น envelope ของ Gcms\Api และ ApiService ห่ออีกชั้น
      // ต้องแกะให้เหลือข้อมูลโครงการก่อนส่งต่อ
      return DataManager.unwrapApiPayload(data);
    } catch (error) {
      this.updateConnectionStatus('disconnected', error.message);
      this.timeline.showNotification(this.timeline.t('Error loading data: {message}', {message: error.message}), 'error');

      const localData = this.loadFromLocalStorage(projectId);

      if (localData) {
        this.timeline.showNotification(this.timeline.t('Loaded data from localStorage'), 'warning');
        return localData;
      }

      return this.createDefaultProject(projectId);
    }
  }

  /**
   * Save data to local storage
   * @param {Object} data - Data to save
   * @returns {boolean} - True if save was successful
   */
  saveToLocalStorage(data) {
    try {
      const projectId = data.projectId || (data.project ? data.project.id : 1);
      const key = `timeline_data_${projectId}`;
      localStorage.setItem(key, JSON.stringify(data));
      return true;
    } catch (error) {
      console.error(this.timeline.t('Error saving to localStorage:'), error);
      return false;
    }
  }

  /**
   * Load data from local storage
   * @param {number|string} projectId - Project ID to load
   * @returns {Object|null} - Project data or null if not found
   */
  loadFromLocalStorage(projectId) {
    try {
      const key = `timeline_data_${projectId}`;
      const data = localStorage.getItem(key);

      if (data) {
        return JSON.parse(data);
      }

      return null;
    } catch (error) {
      console.error(this.timeline.t('Error loading from localStorage:'), error);
      return null;
    }
  }

  /**
   * Create a default project template
   * @param {number|string} projectId - Project ID
   * @returns {Object} - Default project data
   */
  createDefaultProject(projectId) {
    const today = new Date();
    const startDate = this.formatDate(today);

    const endDate = new Date(today);
    endDate.setMonth(endDate.getMonth() + 1);

    return {
      id: projectId,
      name: this.timeline.t('New Project'),
      startDate: startDate,
      endDate: this.formatDate(endDate),
      project: {
        id: projectId,
        name: this.timeline.t('New Project'),
        startDate: startDate,
        endDate: this.formatDate(endDate)
      },
      taskGroups: [],
      tasks: [],
      blocks: [],
      connections: [],
      markers: [],
      holidays: []
    };
  }

  /**
   * Format a date as YYYY-MM-DD
   * @param {Date} date - Date to format
   * @returns {string} - Formatted date
   */
  formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Save timeline data to the server
   * @param {Object} data - Data to save
   * @returns {Promise<Object>} - Server response
   */
  async saveData(data) {
    // Use the API base URL with the save_timeline action
    const saveUrl = `${this.timeline.apiBase}save_timeline`;

    try {
      this.updateConnectionStatus('connecting');

      let result;
      if (window.ApiService) {
        result = await ApiService.post(saveUrl, data);
      } else {
        const response = await fetch(saveUrl, {
          method: 'POST',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(data),
          signal: AbortSignal.timeout(10000)
        });

        if (!response.ok) {
          throw new Error(this.timeline.t('Server responded with status: {status}', {status: response.status}));
        }

        result = await response.json();
      }

      if (result.error) {
        throw new Error(result.error);
      }

      this.updateConnectionStatus('connected');
      this.timeline.showNotification(this.timeline.t('Data saved successfully'), 'success');

      this.saveToLocalStorage(data);

      return {
        success: true,
        data: result
      };
    } catch (error) {
      this.updateConnectionStatus('disconnected', error.message);
      this.timeline.showNotification(this.timeline.t('Error saving data: {message}', {message: error.message}), 'error');

      const localSaved = this.saveToLocalStorage(data);
      if (localSaved) {
        this.timeline.showNotification(this.timeline.t('Data saved to localStorage as backup'), 'info');
      }

      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * Retry loading data after a delay
   * @param {number|string} projectId - Project ID
   * @returns {Promise<Object>} - Project data
   */
  async retryLoading(projectId) {
    if (this.retryAttempts >= this.maxRetryAttempts) {
      this.timeline.showNotification(
        this.timeline.t('Maximum retry attempts reached ({max})', {max: this.maxRetryAttempts}),
        'error'
      );
      return this.createDefaultProject(projectId);
    }

    this.retryAttempts++;

    this.timeline.showNotification(
      this.timeline.t('Retrying connection ({attempt}/{max})...', {
        attempt: this.retryAttempts,
        max: this.maxRetryAttempts
      }),
      'info'
    );

    return new Promise(resolve => {
      setTimeout(async () => {
        try {
          const data = await this.loadProject(projectId);
          resolve(data);
        } catch (error) {
          resolve(this.createDefaultProject(projectId));
        }
      }, this.retryDelay);
    });
  }

  /**
   * ตรวจว่า payload ที่ได้กลับมาเป็นความล้มเหลวหรือไม่
   * timeline endpoints ตอบ HTTP 200 เสมอ แล้วบอกผลผ่าน status/success ใน body
   * @param {Object} payload - เนื้อหา JSON ที่ server ตอบกลับ
   * @returns {string|null} - ข้อความ error หรือ null ถ้าสำเร็จ
   * @private
   */
  _payloadError(payload) {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    if (payload.error) {
      return typeof payload.error === 'string' ? payload.error : 'Unknown error';
    }

    if (payload.status === 'error' || payload.success === false) {
      return payload.message || 'Unknown error';
    }

    return null;
  }

  /**
   * Generic method to send requests to the API
   * @param {string} action - API action to perform
   * @param {string} method - HTTP method (GET, POST)
   * @param {Object} data - Data to send
   * @returns {Promise<Object>} - API response
   */
  async sendRequest(action, method = 'GET', data = null) {
    try {
      this.updateConnectionStatus('connecting');
      let url = `${this.timeline.apiBase}${action}`;

      let result;

      if (window.ApiService) {
        // Use Now.js ApiService
        if (method === 'POST') {
          result = await ApiService.post(url, data);
        } else {
          const params = data ? new URLSearchParams(data).toString() : '';
          result = await ApiService.get(params ? `${url}&${params}` : url);
        }

        // Timeline endpoints answer 200 even when they refuse the write
        // ({status:'error'}), so HTTP status alone is not enough - without this
        // the UI reports "saved" for data the server never stored.
        const failure = this._payloadError(result && result.data !== undefined ? result.data : result);
        if (failure) {
          this.updateConnectionStatus('disconnected', failure);
          return { success: false, error: failure };
        }

        this.updateConnectionStatus('connected');
        return { success: true, data: result };
      } else {
        // Fallback to raw fetch
        let options = {
          method: method,
          headers: {'Content-Type': 'application/json'},
          signal: AbortSignal.timeout(10000)
        };

        if (data && method === 'POST') {
          options.body = JSON.stringify(data);
        } else if (data && method === 'GET') {
          const params = new URLSearchParams(data);
          url += `&${params.toString()}`;
        }

        const response = await fetch(url, options);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const resultData = await response.json();
        const failure = this._payloadError(resultData);
        if (failure) throw new Error(failure);

        this.updateConnectionStatus('connected');
        return { success: true, data: { data: resultData } };
      }
    } catch (error) {
      this.updateConnectionStatus('disconnected', error.message);
      this.timeline.showNotification(
        this.timeline.t('Error in API request: {message}', {message: error.message}),
        'error'
      );
      return { success: false, error: error.message };
    }
  }

  /**
   * Save a specific marker to the server
   * @param {Object} marker - Marker data to save
   * @returns {Promise<Object>} - Server response
   */
  async saveMarker(marker) {
    // ส่ง date ไปด้วยเสมอ: day เป็นดัชนีของ header ซึ่งขึ้นกับช่วงวันที่ที่
    // กำลังแสดงและวันหยุดที่ถูกซ่อน ฝั่ง server แปลงกลับเองไม่ได้
    const date = marker.date ||
      this.timeline.workingDaysManager.markerDayToDate(marker.day) || null;

    return this.sendRequest('update_marker', 'POST', {
      projectId: this.timeline.currentProject?.id || 1,
      marker: {
        id: marker.id,
        tempId: marker.id || marker.tempId,
        day: marker.day,
        date: date,
        row_index: marker.rowIndex || 0,
        color: marker.color,
        // ต้องส่ง icon ไปด้วยทุกครั้ง Model::updateMarker() เขียน icon => null
        // เมื่อไม่ได้รับค่า การลาก marker จึงลบไอคอนทิ้งทุกครั้ง
        icon: marker.icon || null,
        name: marker.name || '',
        description: marker.description || ''
      }
    });
  }

  /**
   * Save a specific connection to the server
   * @param {Object} connection - Connection data to save
   * @returns {Promise<Object>} - Server response
   */
  async saveConnection(connection) {
    try {
      const connectionData = {
        projectId: this.timeline.currentProject?.id || 1,
        connection: {
          id: connection.id,
          tempId: connection.tempId || connection.id,
          fromId: connection.fromId,
          toId: connection.toId,
          gap: connection.gap || 1,
          type: connection.type || 'default'
        }
      };

      const result = await this.sendRequest('update_connection', 'POST', connectionData);

      if (!result.success && result.error) {
        console.error('Failed to save connection:', result.error);
      }

      return result;
    } catch (error) {
      console.error('Error in saveConnection:', error);
      return {success: false, error: error.message};
    }
  }

  /**
   * Save changed blocks to the API
   * @param {Array} changedBlocks - Array of block data to update
   * @param {number|string} projectId - Project ID
   * @param {Object} settings - Settings for the update
   * @returns {Promise<Object>} - API response
   */
  async saveChangedBlocks(changedBlocks, projectId, settings = {}) {
    try {
      // ดึงข้อมูลการเชื่อมโยงปัจจุบัน
      let currentConnections = [];

      // ใช้วิธีการหลายรูปแบบเพื่อให้แน่ใจว่าได้ข้อมูลการเชื่อมโยงที่ถูกต้อง
      if (this.timeline.dependencyManager && typeof this.timeline.dependencyManager.getAllConnections === 'function') {
        currentConnections = this.timeline.dependencyManager.getAllConnections();
      } else if (this.timeline.dependencyManager && this.timeline.dependencyManager.connections) {
        currentConnections = this.timeline.dependencyManager.connections;
      }

      // ถ้ายังไม่มีข้อมูลการเชื่อมโยง ให้ดึงจาก ConnectionManager
      if ((!currentConnections || currentConnections.length === 0) && this.timeline.connectionManager &&
        typeof this.timeline.connectionManager.getConnections === 'function') {
        currentConnections = this.timeline.connectionManager.getConnections();
      }

      // ตรวจสอบการตั้งค่าสำหรับการคำนวณการเชื่อมโยงอย่างละเอียด
      const calculateServerConnections = settings.calculateConnectedBlocks !== undefined
        ? settings.calculateConnectedBlocks
        : (settings.useServerSideConnections && settings.syncConnectedBlocks);

      // แปลงข้อมูลการเชื่อมโยงให้อยู่ในรูปแบบที่เหมาะสมสำหรับ API
      const formattedConnections = currentConnections.map(conn => ({
        id: conn.id || null,
        tempId: conn.tempId || conn.id || null,
        fromId: conn.fromId,
        toId: conn.toId,
        type: conn.type || 'default',
        gap: typeof conn.gap === 'number' ? conn.gap : 1
      }));

      const requestData = {
        projectId: projectId || this.timeline.currentProject?.id || 1,
        blocks: changedBlocks,
        settings: {
          showHolidays: settings.showHolidays !== undefined ? settings.showHolidays : false,
          preventOverlap: settings.preventOverlap !== undefined ? settings.preventOverlap : true,
          syncConnectedBlocks: settings.syncConnectedBlocks !== undefined ? settings.syncConnectedBlocks : false,
          useServerSideConnections: settings.useServerSideConnections !== undefined ? settings.useServerSideConnections : false,
          allowRowCrossing: settings.allowRowCrossing !== undefined ? settings.allowRowCrossing : true,
          calculateConnectedBlocks: calculateServerConnections,
          connections: formattedConnections,
          isDragMode: settings.isDragMode || false,  // เพิ่มการระบุว่าเป็นการเรียกจากโหมด drag หรือไม่
          isResizeMode: settings.isResizeMode || false  // เพิ่มการระบุว่าเป็นการเรียกจากโหมด resize หรือไม่
        }
      };

      const result = await this.sendRequest('update_blocks', 'POST', requestData);

      if (result.success) {
        // ส่งข้อมูลที่ครบถ้วนกลับไป
        return {
          success: true,
          message: 'บันทึกบล็อกทั้งหมดสำเร็จ',
          data: {
            updatedBlocks: result.data.data.updatedBlocks || [],
            updatedConnections: result.data.data.updatedConnections || [],
            affectedBlocks: result.data.data.affectedBlocks || [],
            serverCalculated: result.data.data.serverCalculated || false
          }
        };
      } else {
        console.error('เกิดข้อผิดพลาดในการอัปเดตกลุ่มบล็อก:', result.error);
        return {
          success: false,
          error: result.error,
          message: 'เกิดข้อผิดพลาดจากเซิร์ฟเวอร์'
        };
      }
    } catch (error) {
      console.error('เกิดข้อผิดพลาดในการส่งข้อมูลไปยัง API:', error);
      return {
        success: false,
        error: error.message,
        message: 'เกิดข้อผิดพลาดในการส่งข้อมูล'
      };
    }
  }

  /**
   * Create a new block on server
   * @param {Object} blockData - Block data to create
   * @returns {Promise<Object>} - Server response with new block ID
   */
  async createBlock(blockData) {
    try {
      const data = {
        projectId: this.timeline.currentProject?.id || 1,
        block: blockData
      };

      return await this.sendRequest('create_block', 'POST', data);
    } catch (error) {
      return {success: false, error: error.message};
    }
  }
}

window.TimelineAPIHandler = TimelineAPIHandler;
